import React, {useState, useCallback, useEffect} from 'react'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import {useBoolean} from '../../utils/hooks';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import ButtonContainer from '../../components/Simple/ButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCLink from '../../components/Simple/SMCLink';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {GatewayGetSchema} from '../../api/DBSchema/Gateway';
import EmptyRow from '../../components/Simple/EmptyRow';
import SSLCustomSiteEditorModal from '../PopupPages/SSLCustomSiteEditorModal';
import redXLogo from '../../assets/images/red_x.png';
import greenCheckLogo from '../../assets/images/green_check.png';

const customSiteSchema = GatewayGetSchema.pages.sslInspection.CpSSLBypassCustomSiteSchema;

/**
 * Custom Sites Editor Modal
 * @param {Object} params
 * @param {Boolean} params.customSites
 * @param {Boolean} params.isOpen Is window open
 * @param {Function} params.onRequestClose Window close function
 * @param {Function} params.onSuccess Success callback
 * @param {string} props.baseObjectName
 * @param {string} props.objectType
 */
function SSLCustomSitesEditorModal({
    customSites,
    isOpen,
    onRequestClose,
    onSuccess,
    baseObjectName,
    objectType,
}) {
    const { t } = useTranslation();

    const [modalCustomSites, setModalCustomSites] = useState(customSites); 

    // Watch isFetching value
    useEffect(() => {
        setModalCustomSites(customSites);
    }, [customSites]);

    const [isCustomSiteEditorOpen, openCustomSiteEditor, closeCustomSiteEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);
    
    // Editor objects
    const [editObject, setEditObject] = useState(null);
    const [editObjectIndex, setEditObjectIndex] = useState(null);

    // Callback for when custom site editor is used to edit an existing object
    const editCustomSiteCallback = useCallback(editedCustomSiteObject => {
        const customSitesCopy = [...modalCustomSites];
        customSitesCopy[editObjectIndex] = editedCustomSiteObject;
        setModalCustomSites(customSitesCopy);
        setEditObject(null);
        setEditObjectIndex(null);
        closeCustomSiteEditor();
    }, [modalCustomSites, editObjectIndex, closeCustomSiteEditor]);

     // Callback for when custom site editor is used for a new object
     const newCustomSiteCallback = useCallback(newCustomSiteObject => {
        const customSitesCopy = modalCustomSites ? [...modalCustomSites] : [];
        customSitesCopy.push(newCustomSiteObject);
        setModalCustomSites(customSitesCopy);
        closeCustomSiteEditor();
    }, [modalCustomSites, closeCustomSiteEditor]);

    const deleteSelectedCustomSites = useCallback(() => {
        if (modalCustomSites && selectedObjects.length > 0) {
            setModalCustomSites(modalCustomSites.filter((e, i) => (!selectedObjects.includes(i))));
            setSelectedObjects([]);
        }
    }, [modalCustomSites, selectedObjects]);

    const onCloseCustomSiteEditor = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectIndex(null);
        closeCustomSiteEditor();
    }, [closeCustomSiteEditor]);

    const handleApply = useCallback(() => {
        onSuccess(modalCustomSites);
    }, [modalCustomSites, onSuccess]);

    const handleCancel = useCallback(() => {
       setModalCustomSites(customSites); 
       onRequestClose();
    }, [customSites, onRequestClose]);

    const NameCell = ({value, object, index}) => {
        return (
            <SMCLink
                inline
                onClick={() => {
                    setEditObject(object);
                    setEditObjectIndex(index);
                    openCustomSiteEditor();
                }}
            >
                { value }
            </SMCLink>
        );
    }

    return (
        <>
            <SMCModal
                title={t('SMP.PLAN.SEC_SOFT_BLADES.SSL_WIZ_CUSTOM_SITES')}
                height={505}
                width={500}
                isOpen={isOpen}
                onRequestClose={onRequestClose}
            >
                <SMCModalBody lineHeight="22px" style={{ paddingTop: '5px' }}>
                    <SMCLabel bold>{t('SMP.PLAN.INSPECTION_SSL.BYPASS_SITES_NOTE.LINE1')}</SMCLabel>
                    <EmptyRow/>
                    <ButtonContainer>
                        <SMCButton variant="ghost" icon="new" onClick={openCustomSiteEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                        <SMCButton variant="ghost" icon="delete" onClick={deleteSelectedCustomSites}>{t('SMC_GEN_DELETE')}</SMCButton>
                    </ButtonContainer>
                    <SMCTable
                        columns={[
                            {id: 'name',  accessor: customSiteSchema.name, header: "Name", width: 130, Cell: NameCell },
                            {id: 'value', accessor: customSiteSchema.value, header: "URL",    width: 130 },
                            {id: 'regex', accessor: customSiteSchema.regex, header: "Regular Expression", width: 140,  align: 'center',  Cell: ({value}) => ( <img alt="" src={value ? greenCheckLogo : redXLogo} /> ) }
                        ]}
                        selectedObjects={selectedObjects}
                        setSelectedObjects={setSelectedObjects}
                        content={modalCustomSites}
                    />
                </SMCModalBody>
                <SMCModalButtonContainer>
                    <SMCButton onClick={handleApply}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                        {t('SMC_GEN_APPLY')}
                    </SMCButton>
                    <SMCButton onClick={handleCancel}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                        {t('SMC_GEN_CANCEL')}
                    </SMCButton>
                </SMCModalButtonContainer>
            </SMCModal>
            <SSLCustomSiteEditorModal
               isOpen={isCustomSiteEditorOpen}
               onCancel={onCloseCustomSiteEditor}
               onSuccess={editObject ? editCustomSiteCallback : newCustomSiteCallback}
               defaultObject={editObject}
               baseObjectName={baseObjectName}
               objectType={objectType}
               title={editObject ? t('SMP.PLAN.SEC_SOFT_BLADES.SSL_WIZ_EDIT_CUSTOM_SITE') : t('SMP.PLAN.SEC_SOFT_BLADES.SSL_WIZ_ADD_CUSTOM_SITE')}
            />
        </>
    );
}

export default SSLCustomSitesEditorModal;